/**
 * bibleQueryDetector.js
 * Detects whether a message requires Bible knowledge retrieval.
 *
 * Returns: { detected: boolean, reference: string|null, versions: string[]|null }
 */

import { parseReference, formatReference, detectVersionInText } from "./bibleReferenceParser";

// Keywords that imply a Bible-content request
const BIBLE_KEYWORDS = [
  /vers[íi]culo/i,
  /pasaje\s*b[íi]blico/i,
  /texto\s*b[íi]blico/i,
  /dice\s*la\s*biblia/i,
  /en\s*la\s*biblia/i,
  /b[íi]blica?/i,
  /escritura/i,
  /palabra\s*de\s*dios/i,
  /serm[oó]n/i,
  /pred[ií]ca/i,
  /reflexi[oó]n\s*b[íi]blica?/i,
  /devocional/i,
  /meditaci[oó]n\s*b[íi]blica?/i,
];

/**
 * Detect whether a user message requires Bible RAG.
 *
 * @param {string} text — raw user message
 * @param {string} [intent] — pre-detected intent from orchestrateKevia
 * @returns {{ detected: boolean, reference: string|null, versions: string[]|null }}
 */
export function detectBibleQuery(text, intent) {
  // Fast path: sermon/reflection/bible intents always activate RAG
  if (intent === "sermon" || intent === "reflection" || intent === "bible") {
    const ref = parseReference(text);
    const ver = detectVersionInText(text);
    return {
      detected:  true,
      reference: ref ? formatReference(ref) : null,
      versions:  ver ? [ver] : null,
    };
  }

  // Check explicit reference first (highest confidence)
  const ref = parseReference(text);
  if (ref) {
    const ver = detectVersionInText(text);
    return {
      detected:  true,
      reference: formatReference(ref),
      versions:  ver ? [ver] : null,
    };
  }

  // Check keyword patterns
  const hasKeyword = BIBLE_KEYWORDS.some((re) => re.test(text));
  if (hasKeyword) {
    const ver = detectVersionInText(text);
    return {
      detected:  true,
      reference: null,
      versions:  ver ? [ver] : null,
    };
  }

  return { detected: false, reference: null, versions: null };
}
